import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { variantRouteConfig } from 'src/app/graph/routes';
import { Code } from 'src/components/code/Code';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  queueTestProposalLifecycleNotificationMutation,
  testWebhookChannelMutation,
} from '../../channelCard/notificationActionMenu/NotificationActionMenu';

export function getSubscriptionTypeDisplayName(
  notificationType: FinishNotificationState['notificationType'],
) {
  switch (notificationType) {
    case 'SchemaPublishSubscription':
      return 'schema change';
    case 'ScheduledSummary':
      return 'daily report';
    case 'BuildSubscription':
      return 'build status';
    case 'ProposalLifecycleSubscription':
      return 'schema proposal';
    case undefined:
      throw new Error(`notification type was unexpectedly undefined`);
    default:
      return assertUnreachableOrReturnDefault(
        notificationType,
        'Unknown notification type',
      );
  }
}

export interface FinishNotificationState {
  targetChannel?: GraphQLTypes.AddNotificationWizardChannelFragment;
  notificationType?:
    | 'SchemaPublishSubscription'
    | 'ScheduledSummary'
    | 'BuildSubscription'
    | 'ProposalLifecycleSubscription';
  newSubscriptionId?: string;
}

interface Props {
  isOpen: boolean;
  state: Readonly<FinishNotificationState> | null;
  onClose: () => void;
}

export const testSlackChannelMutation = gql`
  mutation TestSlackChannelMutation(
    $graphId: ID!
    $channelId: ID!
    $notification: SlackNotificationInput!
  ) {
    service(id: $graphId) {
      testSlackChannel(id: $channelId, notification: $notification)
    }
  }
`;

export function FinishNotificationModal({ isOpen, state, onClose }: Props) {
  const { graphId } = useRouteParams(variantRouteConfig);
  const [
    testSlackChannel,
    { loading: testSlackLoading, error: testSlackError },
  ] = useMutation<
    GraphQLTypes.TestSlackChannelMutation,
    GraphQLTypes.TestSlackChannelMutationVariables
  >(testSlackChannelMutation);

  const [
    testWebhookChannel,
    {
      data: testWebhookData,
      loading: testWebhookLoading,
      error: testWebhookError,
    },
  ] = useMutation<
    GraphQLTypes.TestWebhookChannelMutation,
    GraphQLTypes.TestWebhookChannelMutationVariables
  >(testWebhookChannelMutation);
  const [hasTested, setHasTested] = useState(false);

  const [
    queueTestProposalNotificationMutation,
    { loading: testProposalLoading, error: testProposalError },
  ] = useMutation<
    GraphQLTypes.QueueTestProposalLifecycleNotificationMutation,
    GraphQLTypes.QueueTestProposalLifecycleNotificationMutationVariables
  >(queueTestProposalLifecycleNotificationMutation);

  // we can currently test any Slack channel generically
  const canTestSlackChannel =
    state?.targetChannel?.__typename === 'SlackChannel';
  // we can only test a Webhook channel if it's a RegistrySubscription, or if it's a ProposalLifecycleSubscription (although that has a different mutation)
  const canTestWebhookChannel =
    (state?.targetChannel?.__typename === 'WebhookChannel' &&
      state?.notificationType === 'SchemaPublishSubscription') ||
    state?.notificationType === 'ProposalLifecycleSubscription';
  const showTestUX = canTestSlackChannel || canTestWebhookChannel;
  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {hasTested ? 'Test notification sent!' : "You're all set!"}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {!hasTested && (
            <>
              Your new {getSubscriptionTypeDisplayName(state?.notificationType)}{' '}
              notification has been added.{' '}
            </>
          )}
          {showTestUX ? (
            <>
              You can close this dialog now, or {hasTested ? 'resend' : 'send'}{' '}
              a test notification to see how it works.
              {testWebhookData?.service?.testSubscriptionForChannel && (
                <>
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-secondary">
                    Webhook test response
                  </h2>
                  <Code
                    code={testWebhookData.service.testSubscriptionForChannel}
                  />
                </>
              )}
            </>
          ) : (
            <>You can close this dialog now.</>
          )}
          {(testSlackError || testWebhookError || testProposalError) && (
            <ErrorMessage />
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            {showTestUX && (
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            )}
            {showTestUX ? (
              <Button
                variant="primary"
                isLoading={
                  testSlackLoading || testWebhookLoading || testProposalLoading
                }
                loadingText={
                  hasTested
                    ? 'Resend test notification'
                    : 'Send test notification'
                }
                onClick={() => {
                  if (!state?.targetChannel || !state.newSubscriptionId) {
                    // make TS happy
                    return;
                  }
                  if (canTestSlackChannel) {
                    testSlackChannel({
                      variables: {
                        graphId,
                        channelId: state.targetChannel.id,
                        notification: {
                          title: `Test message from Apollo`,
                          fallback: `Test message from Apollo`,
                        },
                      },
                    });
                    setHasTested(true);
                  } else if (canTestWebhookChannel) {
                    if (
                      state.notificationType === 'ProposalLifecycleSubscription'
                    ) {
                      queueTestProposalNotificationMutation({
                        variables: {
                          input: {
                            channelId: state.targetChannel.id,
                            subscriptionId: state.newSubscriptionId,
                          },
                          graphId,
                        },
                      });
                    } else {
                      testWebhookChannel({
                        variables: {
                          graphId,
                          channelId: state.targetChannel.id,
                          subscriptionId: state.newSubscriptionId,
                        },
                      });
                    }
                    setHasTested(true);
                  }
                }}
              >
                {hasTested ? 'Resend' : 'Send'} test notification
              </Button>
            ) : (
              <Button variant="primary" onClick={() => onClose()}>
                Close
              </Button>
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
